import { useState, type ReactNode } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import {
  parseLatexSegments, rewriteRoots, safeTruncateLatex, type LatexSegment,
  hasFigureMarker, splitFigureMarkers, figureMarkersAsText, type QuestionFigure,
} from '@padcanvas/shared';
import { splitBareMath } from './bareMath';
import { ImageViewer } from '../components/ImageViewer';

interface LatexTextProps {
  /** Optional because callers routinely pass fields that may be absent
   *  (`question.correctAnswer` on a STRUCTURED question, projected-away
   *  fields). Rendered as empty rather than throwing. */
  text: string | null | undefined;
  truncate?: number;
  style?: React.CSSProperties;
  expandable?: boolean;
  /** Turns the trailing "…" of a truncated preview into a button that reveals
   *  the rest in place (and back). Only rendered when `truncate` actually cut
   *  something, so short questions show no affordance. Prefer this to
   *  `expandable`, whose modal is light-theme-only. */
  expandInline?: boolean;
  /** Turn bare URLs in the plain-text segments into links that open in a new
   *  tab. Opt-in — LaTeX segments are never touched, so math is unaffected.
   *  Used for teacher-authored reference material. */
  linkify?: boolean;
  /** The question's (or part's) figures, which `[[fig:id]]` markers in the
   *  text place. Markers are rendered with or without it — a marker naming no
   *  figure shows as "[figure missing]", never as the raw marker. Figures no
   *  marker places are the caller's to show after the text (`QuestionFigures`).
   *  See docs/FeatureDesigns/question-rendering/question-figures-target.md. */
  figures?: readonly QuestionFigure[] | null;
}

/** URL matcher for `linkify` — http(s) links and bare `www.` hosts. */
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;

/**
 * Split a plain-text string into text + anchor nodes. Trailing sentence
 * punctuation is kept out of the href (so "see http://x.com." links `http://x.com`
 * and leaves the period as text). Links stop propagation so a click doesn't also
 * fire a parent row/modal handler.
 */
function linkifyToNodes(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index;
    let url = match[0];
    const trail = url.match(/[.,;:!?)\]}'"]+$/);
    const trailing = trail ? trail[0] : '';
    if (trailing) url = url.slice(0, url.length - trailing.length);
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const href = url.startsWith('www.') ? `https://${url}` : url;
    nodes.push(
      <a
        key={`lnk-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-word' }}
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/** Prose/math splitting lives in `@padcanvas/shared` (latex/scan.ts) so this
 *  renderer and the backend `InvalidLatex` diagnosis apply identical rules —
 *  notably, currency dollars are not delimiters. */
const parseLatex = parseLatexSegments;

/** `rewriteRoots`: new text is repaired before it is stored
 *  (shared/src/latex/repair.ts); this is for text stored before that. */
function renderSegments(segments: LatexSegment[], linkify?: boolean) {
  return segments.map((seg, i) => {
    if (seg.type === 'display') {
      try {
        return <BlockMath key={i} math={rewriteRoots(seg.value)} />;
      } catch {
        return <span key={i}>$${seg.value}$$</span>;
      }
    }
    if (seg.type === 'inline') {
      try {
        return <InlineMath key={i} math={rewriteRoots(seg.value)} />;
      } catch {
        return <span key={i}>${seg.value}$</span>;
      }
    }
    if (linkify) return <span key={i}>{linkifyToNodes(seg.value)}</span>;
    return (
      <span key={i}>
        {splitBareMath(seg.value).map((p, j) => (p.math ? <InlineMath key={j} math={p.value} /> : p.value))}
      </span>
    );
  });
}

// ── Pipe-table rendering ──────────────────────────────────────────────
// Imported textbook questions carry tables as pipe-delimited text, e.g.
//   Header A | Header B
//   --- | ---
//   cell 1 | cell 2
// The app has no markdown, so we detect a run of ≥2 pipe rows and render a
// real <table>. Each cell is still run through the LaTeX segment renderer so
// $…$ math and subscripts inside cells work.
type Block = { type: 'text'; value: string } | { type: 'table'; rows: string[][] };

const rowCells = (line: string): string[] => line.split('|').map(c => c.trim());
const isTableRow = (line: string): boolean => line.includes('|') && rowCells(line).length >= 2;
const isSeparatorRow = (cells: string[]): boolean =>
  cells.length > 0 && cells.every(c => /^:?-{2,}:?$/.test(c.trim()));

function splitIntoBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const blocks: Block[] = [];
  let textBuf: string[] = [];
  const flush = () => { if (textBuf.length) { blocks.push({ type: 'text', value: textBuf.join('\n') }); textBuf = []; } };

  for (let i = 0; i < lines.length;) {
    if (isTableRow(lines[i])) {
      let j = i;
      const group: string[] = [];
      while (j < lines.length && isTableRow(lines[j])) { group.push(lines[j]); j++; }
      const dataRows = group.map(rowCells).filter(cells => !isSeparatorRow(cells));
      if (dataRows.length >= 2) {
        flush();
        const cols = Math.max(...dataRows.map(r => r.length));
        const rows = dataRows.map(r => { const rr = r.slice(); while (rr.length < cols) rr.push(''); return rr; });
        blocks.push({ type: 'table', rows });
        i = j;
        continue;
      }
    }
    textBuf.push(lines[i]);
    i++;
  }
  flush();
  return blocks;
}

const CELL_BORDER = '1px solid rgba(128,128,128,0.45)';
function renderTable(rows: string[][], key: number, linkify?: boolean) {
  const [head, ...body] = rows;
  const cellStyle: React.CSSProperties = { border: CELL_BORDER, padding: '4px 10px', textAlign: 'left', verticalAlign: 'top', whiteSpace: 'normal' };
  return (
    <span key={key} style={{ display: 'block', overflowX: 'auto', maxWidth: '100%', margin: '6px 0' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.95em' }}>
        <thead>
          <tr>{head.map((c, ci) => <th key={ci} style={{ ...cellStyle, background: 'rgba(128,128,128,0.12)', fontWeight: 600 }}>{renderSegments(parseLatex(c), linkify)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={cellStyle}>{renderSegments(parseLatex(c), linkify)}</td>)}</tr>)}
        </tbody>
      </table>
    </span>
  );
}

/** Render text that may contain pipe-tables interleaved with LaTeX/plain text. */
function renderContent(text: string, linkify?: boolean) {
  const blocks = splitIntoBlocks(text);
  // Fast path: no table → single pre-wrap span (preserves prior inline behavior).
  if (blocks.length === 1 && blocks[0].type === 'text') {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{renderSegments(parseLatex(blocks[0].value), linkify)}</span>;
  }
  return blocks.map((b, i) =>
    b.type === 'table'
      ? renderTable(b.rows, i, linkify)
      : <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{renderSegments(parseLatex(b.value), linkify)}</span>
  );
}

// ── Figures placed by marker ──────────────────────────────────────────
// A marker alone on its line is a block figure; inside a line it is an inline
// image sized to the text. Split before tables and LaTeX, so each run of text
// between figures renders exactly as it would on its own.

const INLINE_FIGURE_STYLE: React.CSSProperties = {
  height: '1.8em', width: 'auto', maxWidth: '100%', verticalAlign: 'middle', display: 'inline',
};
const BLOCK_FIGURE_STYLE: React.CSSProperties = { maxWidth: '100%', height: 'auto' };

function renderFigure(id: string, display: 'block' | 'inline', figures: readonly QuestionFigure[] | null | undefined, key: number) {
  const fig = figures?.find(f => f.id === id);
  if (!fig) {
    return <span key={key} className="ltx-figure-missing" style={{ opacity: 0.6, fontStyle: 'italic' }}>[figure missing]</span>;
  }
  const alt = fig.alt || `Figure ${fig.id}`;
  return display === 'block'
    ? (
      <span key={key} className="ltx-figure ltx-figure-block" style={{ display: 'block', margin: '8px 0' }}>
        <ImageViewer src={fig.url} alt={alt} style={BLOCK_FIGURE_STYLE} />
      </span>
    )
    : <ImageViewer key={key} src={fig.url} alt={alt} className="ltx-figure ltx-figure-inline" style={INLINE_FIGURE_STYLE} />;
}

/** Full (untruncated) text: figures split out first, the rest as before. */
function renderFull(text: string, figures: readonly QuestionFigure[] | null | undefined, linkify?: boolean) {
  if (!hasFigureMarker(text)) return renderContent(text, linkify);
  return splitFigureMarkers(text).map((seg, i) =>
    seg.type === 'figure'
      ? renderFigure(seg.id, seg.display, figures, i)
      : <span key={i}>{renderContent(seg.value, linkify)}</span>
  );
}

/** Truncate without breaking LaTeX delimiters. Shares the scanner with
 *  `parseLatex` above, so previews cut on the same boundaries the renderer
 *  treats as math. */
const safeTruncate = safeTruncateLatex;

/** Shared look for the inline expand/collapse affordances — a bare text button
 *  that inherits the surrounding type and takes its colour from the theme. */
const INLINE_TOGGLE_STYLE: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, margin: 0,
  font: 'inherit', fontWeight: 600, lineHeight: 'inherit',
  color: 'var(--color-primary, #6366f1)', cursor: 'pointer',
};

export default function LatexText({ text: rawInput, truncate, style, expandable, expandInline, linkify, figures }: LatexTextProps) {
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Absent text is a legitimate state for many callers (a STRUCTURED question has
  // no top-level correctAnswer, a projection may omit a field). Coerce rather than
  // let `undefined.split()` take down the whole page via the error boundary.
  // A preview is one line of text: its figures become a word rather than images.
  const text = truncate ? figureMarkersAsText(rawInput ?? '') : (rawInput ?? '');
  const cutText = truncate ? safeTruncate(text, truncate) : text;
  // safeTruncate returns the input untouched when it fits, and only appends the
  // '...' when it actually cut — so this is the "was anything hidden" test.
  const didTruncate = cutText !== text;
  const isExpanded = !!expandInline && didTruncate && expanded;
  // With `expandInline` the ellipsis becomes the button, so drop the baked-in one.
  const rawText = isExpanded || !didTruncate
    ? text
    : (expandInline ? cutText.slice(0, -3) : cutText);
  // Previews stay inline (no table layout); full text is block-aware. A preview
  // that fit within `truncate` still renders inline — the caller asked for a
  // one-line row, and a short question shouldn't lay out differently.
  const content = !truncate || isExpanded
    ? renderFull(rawText, figures, linkify)
    : <span style={{ whiteSpace: 'pre-wrap' }}>{renderSegments(parseLatex(rawText), linkify)}</span>;

  return (
    <>
      <span
        // Lets a container release a line-clamp it applies to the collapsed
        // preview (see .ql-text / .dp-rq-preview).
        className={isExpanded ? 'ltx-expanded' : undefined}
        style={{ whiteSpace: 'pre-wrap', ...style }}
      >
        {content}
        {expandInline && didTruncate && (
          <button
            type="button"
            // The rows these sit in are themselves clickable (navigate / expand
            // the attempt table); expanding must not also fire that.
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            title={expanded ? 'Show less' : 'Show the full question'}
            aria-expanded={expanded}
            style={INLINE_TOGGLE_STYLE}
          >
            {expanded ? ' less' : '…'}
          </button>
        )}
        {expandable && (
          <button
            onClick={e => { e.stopPropagation(); setShowModal(true); }}
            title="Expand"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginLeft: '4px', padding: '1px 4px', fontSize: '13px',
              background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db',
              borderRadius: '3px', cursor: 'pointer', verticalAlign: 'middle',
              lineHeight: 1,
            }}
          >
            &#x2922;
          </button>
        )}
      </span>
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '10px',
              maxWidth: '900px', width: '90vw', maxHeight: '90vh',
              overflowY: 'auto', padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute', top: '10px', right: '12px',
                background: 'none', border: 'none', fontSize: '20px',
                color: '#6b7280', cursor: 'pointer', lineHeight: 1,
              }}
            >
              &times;
            </button>
            <span style={{ whiteSpace: 'pre-wrap', fontSize: '18px', lineHeight: '1.7' }}>
              {renderFull(text, figures, linkify)}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
